// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function WishFlowTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="WishFlow"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of
        “WishFlow” (the “App”). By downloading, installing, accessing, or using
        the App, you agree to be bound by these Terms. If you do not agree, you
        must uninstall the App and discontinue all use. Where required by
        platform rules or local law, additional terms may apply; in case of
        conflict with App Store or Google Play terms, the platform terms may
        prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        WishFlow is a mobile application for creating and managing wishlists,
        storing gift ideas, tracking items from various online and offline
        stores, and sharing lists with others. You can create lists, add items
        by link or manually, view and update details, and optionally see when
        friends view or interact with shared lists. The App may offer premium
        features such as hidden lists, additional themes, and higher or
        unlimited item limits. The App is not an online store, marketplace, or
        payment processor and does not sell or deliver products.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Accounts, Authentication, and Guest Mode</div>
      <p>
        The App may support sign-in via providers such as Apple, Google, email,
        and an optional guest mode. If you create an account, you are
        responsible for maintaining the confidentiality of your credentials and
        for all activities under your account. You must promptly notify us of
        any suspected unauthorized use.
      </p>
      <p>
        Guest mode may allow limited use without full registration. Data
        created in guest mode may be stored only locally and can be lost if you
        delete the App, reset your device, or if local storage is cleared. To
        sync data across devices and help prevent loss, you may need to convert
        a guest profile into a registered account.
      </p>

      <div className={s.title}>5) Wishlists, Items, and User Content</div>
      <p>
        The wishlists, items, notes, images, links, and other materials you
        store or share through the App (“User Content”) remain yours or your
        licensors’. You are solely responsible for:
      </p>
      <ul>
        <li>
          ensuring that User Content is accurate, up to date, and meaningful for
          your intended use;
        </li>
        <li>
          having all necessary rights and permissions to include photos,
          descriptions, and links (for example, from retailers, brands, and
          people appearing in images);
        </li>
        <li>
          complying with any applicable policies of third-party sites you link
          to (e.g., online stores or marketplaces);
        </li>
        <li>
          removing or updating User Content that is misleading, outdated, or
          no longer desired.
        </li>
      </ul>
      <p>
        You must not use the App to store or share illegal content, hate speech,
        harassment, explicit content where prohibited by law or platform rules,
        or material that infringes third-party rights.
      </p>

      <div className={s.title}>
        6) Product Links, Price Information, and External Stores
      </div>
      <p>
        When you add items via URL, the App or related backend functions may
        attempt to parse product information (for example, title, image, and
        price) from third-party websites. Parsing is performed using metadata
        and page structure that can change at any time. As a result:
      </p>
      <ul>
        <li>
          parsed data may be incomplete, outdated, or inaccurate compared to the
          store page;
        </li>
        <li>
          prices shown in the App are for convenience only and may not reflect
          taxes, shipping, discounts, or final checkout amounts;
        </li>
        <li>
          items may become unavailable, change, or be removed by the seller
          without notice.
        </li>
      </ul>
      <p>
        Purchases are made directly with third-party stores or platforms, not
        through the App. We are not a party to your transactions, do not
        guarantee delivery or quality of products, and are not responsible for
        order issues, returns, or disputes. You should always confirm details
        on the seller’s site before buying.
      </p>

      <div className={s.title}>7) License to Process User Content</div>
      <p>
        To operate the App and provide features such as list management, item
        parsing, previews, and sharing, you grant us and our service providers a
        limited, non-exclusive, worldwide, royalty-free license to process User
        Content solely as necessary to:
      </p>
      <ul>
        <li>store wishlists and items in databases and caches;</li>
        <li>generate thumbnails, covers, and previews;</li>
        <li>
          run link-parsing functions and image-processing tools where explicitly
          enabled;
        </li>
        <li>
          deliver shared lists and notifications to you and, when you choose to
          share, to other users or recipients.
        </li>
      </ul>
      <p>
        We do not claim ownership of your User Content. This license ends within
        a reasonable period after you remove the content from the App or
        uninstall it, except where limited retention is required by law, for
        security, or for backup and operational purposes.
      </p>

      <div className={s.title}>8) Free Tier, Limits, and Premium Features</div>
      <p>
        The App may offer a free tier and a subscription-based premium tier. In
        the free version, functionality may be limited—for example:
      </p>
      <ul>
        <li>caps on the number of items or lists you can create;</li>
        <li>
          restricted access to special themes, designs, or customization options;
        </li>
        <li>
          no access to “Hidden Lists” or similar privacy-enhanced features.
        </li>
      </ul>
      <p>
        The premium tier (“Pro”) may unlock features such as hidden wishlists,
        premium themes, higher or unlimited item counts, extended history, or
        other tools as described in the App and on the paywall screen. We may
        adjust limits, feature sets, and eligibility over time. Where possible,
        we will avoid materially reducing core features you have already paid
        for during your active subscription period.
      </p>

      <div className={s.title}>9) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features may be offered through auto-renewable subscriptions or
        other in-app purchases (“Subscriptions”), such as monthly and yearly
        plans with a free trial. Exact pricing, trial length, currencies, and
        benefits may vary by region and are shown within the App and in the app
        stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are governed by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>10) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features such as Hidden Lists, themes, or higher limits. An internet
        connection may be required to validate or refresh entitlements after
        long offline periods. If a transaction is reversed, refunded, or flagged
        as fraudulent, related entitlements may be limited, suspended, or
        revoked.
      </p>

      <div className={s.title}>11) Sharing, Hidden Lists, and Visibility</div>
      <p>
        The App may allow you to share lists via links or in-app sharing flows.
        When you share a wishlist, content in that list may become visible to
        others according to the sharing mode (for example, public link,
        invite-only, or app-to-app sharing). Anyone who has access to a shared
        link may be able to view items in that list until you revoke or change
        sharing settings.
      </p>
      <p>
        Premium “Hidden Lists” are intended to provide additional privacy within
        the App, such as lists that are not visible to others by default and may
        be accessed only by you unless you explicitly share them. However, no
        digital system can guarantee absolute secrecy or protection against all
        misuse, device compromise, accidental screenshots, or unauthorized
        access to your account. You are responsible for managing sharing
        settings, securing your device, and deciding what to store in each type
        of list.
      </p>

      <div className={s.title}>12) Offline Use, Sync, and Data Loss</div>
      <p>
        The App is designed with an offline-first approach using local storage
        and synchronization with cloud services (for example, Firestore) when
        network connectivity is available. While offline, you may be able to
        create or edit lists and items; changes are typically synced when the
        device reconnects. However:
      </p>
      <ul>
        <li>
          synchronization can fail or be delayed due to connectivity, server
          issues, or conflicts;
        </li>
        <li>
          local data may be lost if you uninstall the App, reset your device, or
          clear storage without full sync;
        </li>
        <li>
          we cannot guarantee recovery of deleted or unsynced data.
        </li>
      </ul>
      <p>
        You are responsible for keeping separate records of any critical
        information and verifying that important changes have been successfully
        saved and synced.
      </p>

      <div className={s.title}>13) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful and reasonable purposes and not
        to:
      </p>
      <ul>
        <li>
          store or share content that is illegal, defamatory, harassing, or
          discriminatory, or that violates privacy or publicity rights;
        </li>
        <li>
          infringe copyrights, trademarks, or other intellectual property rights
          (for example, by using images or logos without permission where
          required);
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls separating
          free and premium features;
        </li>
        <li>
          use automated tools to scrape, copy, or systematically harvest data
          or metadata from the App or its services;
        </li>
        <li>
          use the App for spam, scams, or misleading solicitations.
        </li>
      </ul>

      <div className={s.title}>14) Intellectual Property</div>
      <p>
        You retain ownership of your User Content, subject to rights of third
        parties. All software, user interface designs, templates, themes, icons,
        logos, and other materials provided with the App (excluding your User
        Content and third-party assets) are owned by the publisher or its
        licensors and protected by intellectual property laws. Subject to these
        Terms and any Subscription limitations, you are granted a limited,
        non-exclusive, non-transferable, revocable license to use the App and
        its features to create and manage wishlists for personal and
        non-commercial purposes or for managing your own business gift planning.
      </p>
      <p>
        You may not resell, sublicense, or redistribute the App, its code, or
        its design resources as standalone products or as part of a competing
        service.
      </p>

      <div className={s.title}>15) Third-Party Services and Integrations</div>
      <p>
        The App may rely on third-party services for authentication (Firebase
        Auth), databases and storage (Firestore, Storage), serverless functions
        (for link parsing and business logic), push notifications (FCM),
        analytics and crash reporting (such as Firebase Analytics and
        Crashlytics), and subscription management (RevenueCat). Technical and,
        where applicable, usage data may be shared with these providers to
        operate, secure, and improve the App.
      </p>
      <p>
        These providers act under their own terms and privacy policies. We
        require them by contract to handle data appropriately and only for
        permitted purposes, but we do not control their independent services.
      </p>

      <div className={s.title}>16) Analytics and Notifications</div>
      <p>
        We may collect analytics data to understand how the App is used and to
        improve features, performance, and reliability. Typical events may
        include app_open, create_wishlist, add_item, parse_started, parse_success,
        paywall_view, purchase_start, purchase_success, and similar actions. Data
        may be aggregated or pseudonymized where feasible.
      </p>
      <p>
        With your permission, the App may send push notifications—for example,
        reminders about wishlists, updates when shared lists are viewed or
        claimed, or product-related alerts where available. You can manage
        notification preferences in your device settings and, where offered,
        within the App. Disabling notifications may limit certain reminder
        features but does not affect core functionality.
      </p>

      <div className={s.title}>17) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        Without limiting the foregoing, we do not warrant that:
      </p>
      <ul>
        <li>
          parsed product data (such as price, title, or image) will always be
          accurate, complete, or up to date;
        </li>
        <li>
          all lists, items, or settings will be stored or synced without loss or
          delay under all network and device conditions;
        </li>
        <li>
          the App will operate without interruption, delay, or defects on all
          devices or OS versions.
        </li>
      </ul>

      <div className={s.title}>18) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of wishlists or items), or business interruption
        arising from or related to your use of or inability to use the App, your
        reliance on parsed product data, or your transactions with third-party
        stores. Our total aggregate liability to you for direct damages arising
        out of or relating to the App will be limited to the amount you paid for
        Subscriptions during the twelve (12) months prior to the claim, or the
        minimum amount permitted by applicable law if greater. Some
        jurisdictions do not allow certain limitations or exclusions, so parts
        of this section may not apply to you.
      </p>

      <div className={s.title}>19) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your User Content, your sharing of
        lists, your dealings with third-party stores or services, your breach of
        these Terms, or your violation of any rights of a third party.
      </p>

      <div className={s.title}>20) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        WishFlow, which explains how information is collected, used, stored, and
        shared, including User Content, technical data, and analytics. In case
        of conflict between these Terms and the Privacy Policy regarding data
        processing, the Privacy Policy will govern how we handle personal data,
        while these Terms govern your use of the App and its features. You
        should review the Privacy Policy regularly to understand current
        practices.
      </p>

      <div className={s.title}>21) Changes, Governing Law, and Contact</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or communicated by other reasonable means. By continuing to use the App
        after changes take effect, you accept the updated Terms. If you do not
        agree to the revised Terms, you must stop using the App and uninstall
        it.
      </p>
      <p>
        Applicable law and dispute resolution mechanisms may depend on your
        country of residence and mandatory consumer protection rules. Where
        permitted, you agree to first contact us to attempt to resolve any
        dispute informally. Platform-specific dispute or refund procedures
        (Apple, Google) may also apply to billing matters. Nothing in these
        Terms limits any non-waivable rights you may have under local law.
      </p>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account- or
        purchase-specific matters.
      </p>
    </TermOfUseLayout>
  );
}
